import { forwardRef, useState, type ChangeEvent, type CSSProperties, type InputHTMLAttributes } from 'react';
import { cx } from '@/shared/lib/cx';
import { useFieldAttributes } from './Input';
import s from './CodeInput.module.css';

export interface CodeInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'maxLength' | 'size'> {
  /** Количество цифр (по умолчанию 6 — код из SMS или приложения-аутентификатора). */
  length?: number;
  value: string;
  onChange: (value: string) => void;
  /** Вызывается, когда введены все цифры. */
  onComplete?: (value: string) => void;
  /** Режим PIN: цифры скрыты точками, автозаполнение из SMS выключено. */
  mask?: boolean;
  invalid?: boolean;
}

/**
 * Поле кода из N цифр. Одно настоящее поле ввода (автозаполнение `one-time-code`, вставка,
 * экранные дикторы) и N ячеек для отображения.
 */
export const CodeInput = forwardRef<HTMLInputElement, CodeInputProps>(function CodeInput(
  { length = 6, value, onChange, onComplete, mask = false, invalid: invalidProp, id: idProp, disabled: disabledProp, required: requiredProp, className, onFocus, onBlur, onSelect, ...rest },
  ref,
) {
  const { id, invalid, required, disabled, describedBy } = useFieldAttributes({
    id: idProp,
    invalid: invalidProp,
    required: requiredProp,
    disabled: disabledProp,
    'aria-describedby': rest['aria-describedby'],
  });
  const [focused, setFocused] = useState(false);
  const digits = value.replace(/\D/g, '').slice(0, length);
  const activeIndex = Math.min(digits.length, length - 1);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value.replace(/\D/g, '').slice(0, length);
    onChange(next);
    if (next.length === length && next !== digits) onComplete?.(next);
  };

  const moveCaretToEnd = (input: HTMLInputElement) => {
    const end = input.value.length;
    if (input.selectionStart !== end || input.selectionEnd !== end) input.setSelectionRange(end, end);
  };

  return (
    <div
      className={cx(s.root, invalid && s.invalid, disabled && s.disabled, className)}
      style={{ '--cells': length } as CSSProperties}
    >
      <input
        ref={ref}
        id={id}
        className={s.input}
        type={mask ? 'password' : 'text'}
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete={mask ? 'off' : 'one-time-code'}
        maxLength={length}
        value={digits}
        onChange={handleChange}
        onFocus={(event) => {
          setFocused(true);
          moveCaretToEnd(event.currentTarget);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        onSelect={(event) => {
          moveCaretToEnd(event.currentTarget);
          onSelect?.(event);
        }}
        disabled={disabled}
        required={required}
        spellCheck={false}
        {...rest}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
      {Array.from({ length }, (_, index) => {
        const char = digits[index];
        const isActive = focused && index === activeIndex && !disabled;
        return (
          <span key={index} className={cx(s.cell, isActive && s.active)} aria-hidden="true">
            {char ? (mask ? '•' : char) : isActive ? <span className={s.caret} /> : null}
          </span>
        );
      })}
    </div>
  );
});
